import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import Modal from 'react-modal';
import Swal from 'sweetalert2';

import {
  BotonNaranja,
  CalendarField,
  CalendarFieldLabel,
  CalendarInput,
  modalStyles,
  mobileModalStyles,
  CalendarTextarea,
} from '../../Css/StyledCalendarBase.js';

import {
  StyledModalDynamic,
  CalendarModalCard,
  CalendarModalTitle,
  CalendarModalDescription,
  CalendarModalActions,
  CalendarModalCloseButton,
  CalendarFormLayout,
  CalendarSelect,
  CurrencyInputContainer,
  CurrencyInput,
  CurrencySymbol,
  CancelButton,
  TurnTypeOptionsGrid,
  TurnTypeOptionButton,
  TurnTypeOptionTitle,
  TurnTypeOptionDescription,
} from '../../Css/StyledCalendarModal.js';

import PatientSearch from './PatientSearch.js';
import { CloseIcon } from './CalendarIcons.js';

import {
  NOTIFICATION_STATUS,
  canManuallyRequeueNotification,
  getManualRequeueButtonText,
  getNotificationActionMessage,
  getNotificationErrorMessage,
  getNotificationPrimaryEmail,
  getNotificationStatus,
  getNotificationStatusMessage,
  isSpamNotification
} from '../../../utils/notificationCalendarUtils.js';

import {
  TURN_TYPES,
  TURN_TYPE_OPTIONS,
  normalizeTurnType
} from '../../../constants/calendarConstants.js';

const normalizeRepeatValue = (repeatValue) => {
  if (repeatValue === 'semanalmente') {
    return 'semanal';
  }

  if (repeatValue === 'quincenalmente') {
    return 'quincenal';
  }

  return repeatValue || 'no';
};

const getRepeatTypeLabel = (repeatValue) => {
  const normalizedRepeat = normalizeRepeatValue(repeatValue);

  if (normalizedRepeat === 'semanal') {
    return 'semanal';
  }

  if (normalizedRepeat === 'quincenal') {
    return 'quincenal';
  }

  return '';
};

const formatDisplayDate = (dateString) => {
  if (!dateString) {
    return '';
  }

  const [year, month, day] = dateString.split('-');

  if (!year || !month || !day) {
    return dateString;
  }

  return `${day}/${month}/${year}`;
};

const getEstadoNotificacion = (turno) => {
  return getNotificationStatus(turno);
};

// Define si los campos de notificación pueden editarse.
// REBOTADO y ERROR se habilitan porque ahí vamos a permitir corregir/reencolar.
// SPAM queda bloqueado: no conviene reenviar directo a una dirección que marcó spam.
const puedeEditarNotificacion = (turno, options = {}) => {
  const { turnoYaPaso = false } = options;
  const estadoNotificacion = getEstadoNotificacion(turno);

  if (turnoYaPaso) {
    return false;
  }

  if (
    estadoNotificacion === NOTIFICATION_STATUS.SIN_NOTIFICACION ||
    estadoNotificacion === NOTIFICATION_STATUS.PROGRAMADA
  ) {
    return true;
  }

  if (
    estadoNotificacion === NOTIFICATION_STATUS.REBOTADO ||
    estadoNotificacion === NOTIFICATION_STATUS.ERROR
  ) {
    return true;
  }

  return false;
};

const createInitialFormData = () => ({
  patient: '',
  comentario: '',
  selectedPatient: null,
  day: '',
  startTime: '',
  endTime: '',
  fecha_notificacion: '',
  meetingLink: '',
  honorarios: '',
  repeat: 'no',
  email_1: '',
  email_2: '',
  notificar: 'no',
  notificar_cantidad: '1',
  estado: 'PENDIENTE',
  tipo_turno: TURN_TYPES.SESION_PACIENTE,
  aplicar_serie_desde_este_turno: false,

  modalidad_turno: 'presencial',
  consultorio_id: '',
  direccion_consultorio: '',
  incluir_telefono_consultorio: 'no',
  telefono_consultorio: ''
});

const CalendarModal = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  onResendNotification = null,
  onOpenClinicalHistory = null,
  onOpenPatientStats = null,
  prefilledData = null,
  mode = 'create',
  appointmentToEdit = null,
  canDeleteAppointment = true
}) => {
  const isEditMode = mode === 'edit';

  const appointmentRepeat = normalizeRepeatValue(
    appointmentToEdit?.repeat_type ||
    appointmentToEdit?.repeat ||
    appointmentToEdit?.repetir_turno ||
    appointmentToEdit?.tipo_repeticion ||
    'no'
  );

  const isSeriesTurn = Boolean(
    isEditMode &&
    appointmentToEdit &&
    appointmentToEdit.repeat_group_id
  );

  const repeatTypeLabel = getRepeatTypeLabel(appointmentRepeat);

  const canModifySeriesConditions = Boolean(
    appointmentToEdit?.puede_modificar_condiciones_serie
  );

  const serieFechaOrigen =
    appointmentToEdit?.serie_fecha_origen ||
    appointmentToEdit?.fecha_origen_serie ||
    null;

  const serieHoraOrigen =
    appointmentToEdit?.serie_hora_inicio_origen ||
    appointmentToEdit?.hora_inicio_origen_serie ||
    null;

  const [isMobile, setIsMobile] = useState(false);

  const [formData, setFormData] = useState(createInitialFormData);

  const shouldBlockDate = Boolean(
    isEditMode &&
    isSeriesTurn &&
    formData.aplicar_serie_desde_este_turno &&
    !canModifySeriesConditions
  );

  const pacientes = useSelector((state) => state.informacion.lista_pacientes || []);

  const consultoriosProfesional = useSelector(
    (state) => state.usuario.consultorios_profesional || []
  );

  const consultoriosActivos = consultoriosProfesional.filter(
    (consultorio) => consultorio && consultorio.activo !== false
  );

  const consultorioDefault = consultoriosActivos.find(
    (consultorio) => Boolean(consultorio.es_default)
  );

  const buscarConsultorioPorId = (consultorioId) => {
    return consultoriosActivos.find(
      (consultorio) => String(consultorio.id) === String(consultorioId)
    );
  };

  const timeSlots = [
    '06:00','06:05','06:10','06:15','06:20','06:25','06:30','06:35','06:40','06:45','06:50','06:55',
    '07:00','07:05','07:10','07:15','07:20','07:25','07:30','07:35','07:40','07:45','07:50','07:55',
    '08:00','08:05','08:10','08:15','08:20','08:25','08:30','08:35','08:40','08:45','08:50','08:55',
    '09:00','09:05','09:10','09:15','09:20','09:25','09:30','09:35','09:40','09:45','09:50','09:55',
    '10:00','10:05','10:10','10:15','10:20','10:25','10:30','10:35','10:40','10:45','10:50','10:55',
    '11:00','11:05','11:10','11:15','11:20','11:25','11:30','11:35','11:40','11:45','11:50','11:55',
    '12:00','12:05','12:10','12:15','12:20','12:25','12:30','12:35','12:40','12:45','12:50','12:55',
    '13:00','13:05','13:10','13:15','13:20','13:25','13:30','13:35','13:40','13:45','13:50','13:55',
    '14:00','14:05','14:10','14:15','14:20','14:25','14:30','14:35','14:40','14:45','14:50','14:55',
    '15:00','15:05','15:10','15:15','15:20','15:25','15:30','15:35','15:40','15:45','15:50','15:55',
    '16:00','16:05','16:10','16:15','16:20','16:25','16:30','16:35','16:40','16:45','16:50','16:55',
    '17:00','17:05','17:10','17:15','17:20','17:25','17:30','17:35','17:40','17:45','17:50','17:55',
    '18:00','18:05','18:10','18:15','18:20','18:25','18:30','18:35','18:40','18:45','18:50','18:55',
    '19:00','19:05','19:10','19:15','19:20','19:25','19:30','19:35','19:40','19:45','19:50','19:55',
    '20:00','20:05','20:10','20:15','20:20','20:25','20:30','20:35','20:40','20:45','20:50','20:55',
    '21:00','21:05','21:10','21:15','21:20','21:25','21:30','21:35','21:40','21:45','21:50','21:55',
    '22:00','22:05','22:10','22:15','22:20','22:25','22:30','22:35','22:40','22:45','22:50','22:55',
    '23:00','23:05','23:10','23:15','23:20','23:25','23:30'
  ];

  const currentModalStyles = isMobile ? mobileModalStyles : modalStyles;

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 480);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const getTodayLocalString = () => {
    const today = new Date();

    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const parseLocalDate = (dateString) => {
    if (!dateString) {
      return null;
    }

    const [year, month, day] = dateString.split('-').map(Number);

    if (!year || !month || !day) {
      return null;
    }

    return new Date(year, month - 1, day);
  };

  const parseLocalDateTime = (dateString, timeString) => {
    if (!dateString || !timeString) {
      return null;
    }

    const [year, month, day] = dateString.split('-').map(Number);
    const [hours, minutes] = timeString.split(':').map(Number);

    if (
      !year ||
      !month ||
      !day ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      return null;
    }

    return new Date(year, month - 1, day, hours, minutes, 0, 0);
  };

  const turnoYaComenzoOPaso = ({ fecha_turno, hora_inicio }) => {
    const fechaHoraTurno = parseLocalDateTime(fecha_turno, hora_inicio);

    if (!fechaHoraTurno) {
      return false;
    }

    const ahora = new Date();

    return fechaHoraTurno <= ahora;
  };

  const getTodayOnly = () => {
    const today = new Date();

    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Se recalcula con la fecha/hora actual del formulario.
  // Sirve para bloquear reenvío si el turno ya empezó o ya pasó.
  const appointmentStartedOrPassed = Boolean(
    isEditMode &&
    turnoYaComenzoOPaso({
      fecha_turno: formData.day || appointmentToEdit?.fecha_turno,
      hora_inicio: formData.startTime || appointmentToEdit?.hora_inicio
    })
  );

  const estadoTurnoParaRepeat = String(
    formData.estado ||
    appointmentToEdit?.estado ||
    'PENDIENTE'
  )
    .trim()
    .toUpperCase();

  const estadoBloqueaRepeat = [
    'REALIZADO',
    'CANCELADO',
    'NO_ASISTIO',
    'BORRADO'
  ].includes(estadoTurnoParaRepeat);

  const puedeEditarRepeatTurnoUnico = Boolean(
    isEditMode &&
    !isSeriesTurn &&
    !appointmentStartedOrPassed &&
    !estadoBloqueaRepeat
  );

  const puedeEditarRepeatSerie = Boolean(
    isEditMode &&
    isSeriesTurn &&
    canModifySeriesConditions &&
    !appointmentStartedOrPassed &&
    !estadoBloqueaRepeat
  );

  const shouldBlockRepeat = Boolean(
    isEditMode &&
    !puedeEditarRepeatTurnoUnico &&
    !puedeEditarRepeatSerie
  );

  const estadoNotificacionActual = getEstadoNotificacion(appointmentToEdit);

  const canShowManualRequeueButton = Boolean(
    isEditMode &&
    appointmentToEdit &&
    canManuallyRequeueNotification(appointmentToEdit) &&
    !appointmentStartedOrPassed
  );

  const shouldShowSpamWarning = Boolean(
    isEditMode &&
    appointmentToEdit &&
    isSpamNotification(appointmentToEdit)
  );

  const mensajeEstadoNotificacion =
    getNotificationStatusMessage(appointmentToEdit);

  const mensajeErrorNotificacion =
    getNotificationErrorMessage(appointmentToEdit);

  const mensajeAccionNotificacion =
    getNotificationActionMessage(appointmentToEdit);

  const emailPrincipalNotificacion =
    getNotificationPrimaryEmail(appointmentToEdit);

  const shouldShowClinicalHistoryButton = Boolean(
    isEditMode &&
    appointmentToEdit?.tiene_historia_clinica &&
    (
      appointmentToEdit?.historia_clinica_id ||
      appointmentToEdit?.hc_id
    ) &&
    onOpenClinicalHistory
  );

  const shouldShowPatientStatsButton = Boolean(
    isEditMode &&
    appointmentToEdit &&
    onOpenPatientStats
  );

  const selectedTurnType = normalizeTurnType(formData.tipo_turno);

  const isPatientSession =
    selectedTurnType === TURN_TYPES.SESION_PACIENTE;

  const patientLabel = isPatientSession
    ? 'Paciente *'
    : 'Paciente asociado *';

  const patientPlaceholder = isPatientSession
    ? 'Buscar paciente por nombre o email...'
    : 'Buscar paciente asociado por nombre o email...';

  const getTurnTypeDescription = (turnTypeValue) => {
    if (turnTypeValue === TURN_TYPES.SESION_PACIENTE) {
      return 'Trabajo directo con el paciente.';
    }

    if (turnTypeValue === TURN_TYPES.REUNION_PADRES) {
      return 'Encuentro asociado al paciente.';
    }

    if (turnTypeValue === TURN_TYPES.REUNION_COLEGIO) {
      return 'Articulación con escuela/colegio.';
    }

    if (turnTypeValue === TURN_TYPES.REUNION_PROFESIONALES) {
      return 'Intercambio con otros profesionales.';
    }

    return '';
  };

  const handleTurnTypeChange = (turnTypeValue) => {
    handleInputChange('tipo_turno', normalizeTurnType(turnTypeValue));
  };

  const debeUsarConsultorio = (tipoTurno, modalidadTurno) => {
    return (
      normalizeTurnType(tipoTurno) === TURN_TYPES.SESION_PACIENTE &&
      modalidadTurno === 'presencial' &&
      consultoriosActivos.length > 0
    );
  };

  const limpiarDatosConsultorio = (updated) => {
    updated.consultorio_id = '';
    updated.direccion_consultorio = '';
    updated.telefono_consultorio = '';
    updated.incluir_telefono_consultorio = 'no';

    return updated;
  };

  const completarDatosConsultorio = (updated, consultorioId) => {
    const consultorio = buscarConsultorioPorId(consultorioId);

    if (!consultorio) {
      updated.consultorio_id = '';
      updated.direccion_consultorio = '';
      updated.telefono_consultorio = '';
      return updated;
    }

    updated.consultorio_id = String(consultorio.id);
    updated.direccion_consultorio = consultorio.direccion || '';
    updated.telefono_consultorio = consultorio.telefono || '';

    return updated;
  };

  const handlePatientChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      patient: value || ''
    }));
  };

  const handlePatientSelect = (patient) => {
    const fullName = `${patient.nombre || ''} ${patient.apellido || ''}`.trim();

    setFormData((prev) => ({
      ...prev,
      selectedPatient: patient,
      patient: fullName,
      email_1: patient.email_1 || patient.email || prev.email_1 || '',
      email_2: patient.email_2 || prev.email_2 || ''
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      let updated = {
        ...prev,
        [field]: value ?? ''
      };

      if (field === 'day') {
        updated.startTime = '';
        updated.endTime = '';
        updated.fecha_notificacion = '';
      }

      if (field === 'startTime' && value) {
        const [h, m] = value.split(':').map(Number);

        const startDate = new Date(0, 0, 0, h, m);
        const endDate = new Date(startDate.getTime() + 45 * 60000);

        const endHours = endDate.getHours().toString().padStart(2, '0');
        const endMinutes = endDate.getMinutes().toString().padStart(2, '0');

        updated.endTime = `${endHours}:${endMinutes}`;
      }

      if (field === 'notificar' && value === 'no') {
        updated.fecha_notificacion = '';
        updated.email_1 = '';
        updated.email_2 = '';
        updated.notificar_cantidad = '1';

        // Importante:
        // No borramos consultorio_id ni dirección de sede al apagar notificación.
        // Así el turno pasado se puede guardar como registro administrativo
        // y, si luego vuelve a activar notificación en un turno futuro,
        // no pierde los datos del consultorio seleccionado.
        if (!updated.consultorio_id) {
          updated.direccion_consultorio = '';
          updated.telefono_consultorio = '';
          updated.incluir_telefono_consultorio = 'no';
        }
      }

      if (field === 'notificar_cantidad' && value === '1') {
        updated.email_2 = '';
      }

      if (field === 'tipo_turno') {
        const nuevoTipoTurno = normalizeTurnType(value);

        if (nuevoTipoTurno !== TURN_TYPES.SESION_PACIENTE) {
          updated = limpiarDatosConsultorio(updated);
        }

        if (
          nuevoTipoTurno === TURN_TYPES.SESION_PACIENTE &&
          updated.modalidad_turno === 'presencial' &&
          !updated.consultorio_id &&
          consultorioDefault
        ) {
          updated = completarDatosConsultorio(updated, consultorioDefault.id);
        }
      }

      if (field === 'modalidad_turno' && value === 'virtual') {
        updated = limpiarDatosConsultorio(updated);
      }

      if (
        field === 'modalidad_turno' &&
        value === 'presencial' &&
        normalizeTurnType(updated.tipo_turno) === TURN_TYPES.SESION_PACIENTE &&
        !updated.consultorio_id &&
        consultorioDefault
      ) {
        updated = completarDatosConsultorio(updated, consultorioDefault.id);
      }

      if (field === 'consultorio_id') {
        updated = completarDatosConsultorio(updated, value);
      }

      if (field === 'incluir_telefono_consultorio' && value === 'no') {
        updated.telefono_consultorio = '';
      }

      return updated;
    });
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (mode === 'create') {
      const consultorioInicial = consultorioDefault || null;

      setFormData({
        ...createInitialFormData(),
        day: prefilledData?.day || '',
        startTime: prefilledData?.startTime || '',
        endTime: prefilledData?.endTime || '',
        tipo_turno: normalizeTurnType(prefilledData?.tipo_turno),

        consultorio_id: consultorioInicial
          ? String(consultorioInicial.id)
          : '',

        direccion_consultorio: consultorioInicial?.direccion || '',
        telefono_consultorio: consultorioInicial?.telefono || ''
      });

      return;
    }

    if (mode === 'edit' && appointmentToEdit) {
      const patientId =
        appointmentToEdit.paciente_id ||
        appointmentToEdit.id_paciente ||
        '';

      const selectedPatient = pacientes.find(
        (patient) => String(patient.id_paciente) === String(patientId)
      );

      const patientName =
        appointmentToEdit.paciente_nombre_completo ||
        `${appointmentToEdit.paciente_nombre || ''} ${appointmentToEdit.paciente_apellido || ''}`.trim() ||
        `${selectedPatient?.nombre || ''} ${selectedPatient?.apellido || ''}`.trim() ||
        'Paciente sin nombre';

      const appointmentEstadoNotificacion =
        getEstadoNotificacion(appointmentToEdit);

      const appointmentNotificar =
        appointmentToEdit.notificar === 'si' ||
        appointmentEstadoNotificacion !== NOTIFICATION_STATUS.SIN_NOTIFICACION
          ? 'si'
          : 'no';

      const appointmentModalidad =
        appointmentToEdit.modalidad_turno ||
        appointmentToEdit.modalidad ||
        '';

      const appointmentTelefonoConsultorio =
        appointmentToEdit.telefono_consultorio ||
        appointmentToEdit.telefono_contacto ||
        appointmentToEdit.consultorio_telefono ||
        '';

      const appointmentIncluirTelefono =
        appointmentToEdit.incluir_telefono_consultorio ||
        appointmentToEdit.incluir_telefono ||
        (appointmentTelefonoConsultorio ? 'si' : 'no');

      setFormData({
        patient: patientName,
        comentario: appointmentToEdit.comentario || '',
        selectedPatient: selectedPatient || {
          id_paciente: patientId,
          nombre: patientName,
          apellido: ''
        },

        day: appointmentToEdit.fecha_turno || '',
        startTime: appointmentToEdit.hora_inicio || '',
        endTime: appointmentToEdit.hora_fin || '',

        fecha_notificacion: appointmentToEdit.fecha_notificacion || '',
        meetingLink: appointmentToEdit.link_reunion || '',
        honorarios:
          appointmentToEdit.honorarios ??
          appointmentToEdit.valor_sesion ??
          '',

        repeat: normalizeRepeatValue(
          appointmentToEdit.repeat_type ||
          appointmentToEdit.repeat ||
          appointmentToEdit.repetir_turno ||
          appointmentToEdit.tipo_repeticion ||
          'no'
        ),

        email_1: appointmentToEdit.email_1 || '',
        email_2: appointmentToEdit.email_2 || '',
        notificar: appointmentNotificar,
        notificar_cantidad:
          appointmentNotificar === 'si'
            ? String(
                appointmentToEdit.notificar_cantidad ||
                (appointmentToEdit.email_2 ? 2 : 1)
              )
            : '1',

        estado: appointmentToEdit.estado || 'PENDIENTE',
        tipo_turno: normalizeTurnType(appointmentToEdit.tipo_turno),
        aplicar_serie_desde_este_turno: false,

        modalidad_turno: appointmentModalidad,

        consultorio_id:
          appointmentModalidad === 'presencial' && appointmentToEdit.consultorio_id
            ? String(appointmentToEdit.consultorio_id)
            : '',

        direccion_consultorio:
          appointmentModalidad === 'presencial'
            ? appointmentToEdit.direccion_consultorio ||
              appointmentToEdit.consultorio_direccion ||
              ''
            : '',

        incluir_telefono_consultorio: appointmentIncluirTelefono,

        telefono_consultorio:
          appointmentIncluirTelefono === 'si'
            ? appointmentTelefonoConsultorio ||
              appointmentToEdit.consultorio_telefono ||
              ''
            : ''
      });
    }
  }, [isOpen, mode, prefilledData, appointmentToEdit, pacientes, consultorioDefault]);

  const isNotificationLocked = () => {
    if (!isEditMode) {
      return false;
    }

    if (!appointmentToEdit) {
      return false;
    }

    if (appointmentStartedOrPassed) {
      return true;
    }

    return !puedeEditarNotificacion(appointmentToEdit, {
      turnoYaPaso: appointmentStartedOrPassed
    });
  };

  const notificationLocked = isNotificationLocked();

  const shouldDisableNotificationToggle = Boolean(
    notificationLocked && !appointmentStartedOrPassed
  );

  const shouldShowNotificationFields = Boolean(
    formData.notificar === 'si'
  );

  const shouldShowSecondEmail = Boolean(
    shouldShowNotificationFields &&
    (
      formData.notificar_cantidad === '2' ||
      formData.email_2
    )
  );

  const shouldShowMeetingLink = Boolean(
    formData.modalidad_turno === 'virtual'
  );

  const shouldShowConsultorioSelector = Boolean(
    debeUsarConsultorio(
      formData.tipo_turno,
      formData.modalidad_turno
    )
  );

  const shouldShowConsultorioAddress = Boolean(
    shouldShowNotificationFields &&
    formData.modalidad_turno === 'presencial'
  );

  const shouldDisableTelefonoConsultorio = Boolean(
    notificationLocked ||
    formData.incluir_telefono_consultorio !== 'si'
  );

  const handleManualRequeueNotification = async () => {
    if (!appointmentToEdit) {
      return;
    }

    if (appointmentStartedOrPassed) {
      await Swal.fire({
        icon: 'warning',
        title: 'No se puede reenviar',
        text: 'El turno ya comenzó o ya pasó. Ya no se puede volver a poner la notificación en cola.',
        confirmButtonText: 'OK'
      });

      return;
    }

    const idTurno =
      appointmentToEdit.id_turno ||
      appointmentToEdit.id;

    if (!idTurno) {
      await Swal.fire({
        icon: 'error',
        title: 'No se encontró el turno',
        text: 'No se pudo identificar el turno para reenviar la notificación.',
        confirmButtonText: 'OK'
      });

      return;
    }

    if (formData.notificar !== 'si') {
      await Swal.fire({
        icon: 'warning',
        title: 'Activá la notificación',
        text: 'Para reenviar, la opción “Notificar por email” debe estar en “Sí”.',
        confirmButtonText: 'OK'
      });

      return;
    }

    if (!formData.fecha_notificacion) {
      await Swal.fire({
        icon: 'warning',
        title: 'Falta la fecha de notificación',
        text: 'Seleccioná la fecha en la que querés programar nuevamente el email.',
        confirmButtonText: 'OK'
      });

      return;
    }

    if (!formData.email_1 || !isValidEmail(formData.email_1.trim())) {
      await Swal.fire({
        icon: 'warning',
        title: 'Revisá el email',
        text: 'Cargá un email válido para volver a enviar la notificación.',
        confirmButtonText: 'OK'
      });

      return;
    }

    if (
      String(formData.notificar_cantidad) === '2' &&
      (!formData.email_2 || !isValidEmail(formData.email_2.trim()))
    ) {
      await Swal.fire({
        icon: 'warning',
        title: 'Revisá el segundo email',
        text: 'Cargá un segundo email válido o cambiá la cantidad de personas a 1.',
        confirmButtonText: 'OK'
      });

      return;
    }

    const payload = {
      id_turno: idTurno,
      fecha_notificacion: formData.fecha_notificacion,
      email_1: formData.email_1.trim(),
      email_2: String(formData.notificar_cantidad) === '2'
        ? formData.email_2.trim()
        : '',
      notificar_cantidad: Number(formData.notificar_cantidad || 1)
    };

    if (onResendNotification) {
      await onResendNotification(payload);
      return;
    }

    await Swal.fire({
      icon: 'info',
      title: 'Reenvío preparado',
      html: `
        <div style="text-align:left; font-size:14px; line-height:1.45;">
          <p>
            El frontend ya preparó los datos para volver a poner la notificación en cola.
          </p>

          <p>
            Falta conectar el endpoint del backend.
          </p>

          <pre style="white-space:pre-wrap; background:#F3F4F6; padding:10px; border-radius:8px; font-size:12px;">${JSON.stringify(payload, null, 2)}</pre>
        </div>
      `,
      confirmButtonText: 'OK'
    });
  };

  const handleSubmit = async () => {
    const selectedPatientId = formData.selectedPatient?.id_paciente;

    if (
      !selectedPatientId ||
      !formData.day ||
      !formData.startTime ||
      !formData.endTime
    ) {
      alert('Por favor seleccioná un paciente válido y completá día, inicio y fin.');
      return false;
    }

    if (
      isEditMode &&
      isSeriesTurn &&
      formData.aplicar_serie_desde_este_turno &&
      !canModifySeriesConditions &&
      formData.day !== appointmentToEdit?.fecha_turno
    ) {
      alert('La fecha de una serie solo puede modificarse desde el turno original si todavía no ocurrió.');
      return false;
    }

    if (
      formData.notificar === 'si' &&
      formData.modalidad_turno === 'virtual' &&
      !formData.meetingLink.trim()
    ) {
      alert('Por favor completá el link de reunión para notificar un turno virtual.');
      return false;
    }

    const esTurnoNuevo = !isEditMode;

    if (
      esTurnoNuevo &&
      shouldShowConsultorioSelector &&
      !formData.consultorio_id
    ) {
      alert('Por favor seleccioná el consultorio o sede de atención.');
      return false;
    }

    const selectedDate = parseLocalDate(formData.day);

    if (!selectedDate) {
      alert('La fecha del turno no es válida.');
      return false;
    }

    if (
      formData.startTime &&
      formData.endTime &&
      formData.startTime >= formData.endTime
    ) {
      alert('La hora de inicio debe ser anterior a la hora de fin');
      return false;
    }

    // Regla importante:
    // Se puede crear o editar un turno pasado como registro administrativo.
    // Lo único que NO se permite es guardarlo con notificación activa.
    if (
      formData.notificar === 'si' &&
      turnoYaComenzoOPaso({
        fecha_turno: formData.day,
        hora_inicio: formData.startTime
      })
    ) {
      alert('El turno ya comenzó o ya pasó. Por favor eliminá la notificación para poder guardar el turno.');
      return false;
    }
    if (formData.notificar === 'si' && !notificationLocked) {
      if (!formData.fecha_notificacion) {
        alert('Por favor seleccioná qué día querés enviar la notificación.');
        return false;
      }

      if (!formData.email_1.trim()) {
        alert('Por favor completá el email del tutor.');
        return false;
      }

      if (!isValidEmail(formData.email_1.trim())) {
        alert('El email del tutor no tiene un formato válido.');
        return false;
      }

      if (formData.notificar_cantidad === '2') {
        if (!formData.email_2.trim()) {
          alert('Por favor completá el email del segundo tutor.');
          return false;
        }

        if (!isValidEmail(formData.email_2.trim())) {
          alert('El email del segundo tutor no tiene un formato válido.');
          return false;
        }
      }

      if (
        formData.modalidad_turno === 'presencial' &&
        !formData.direccion_consultorio.trim()
      ) {
        alert('Por favor completá la dirección del consultorio.');
        return false;
      }

      if (
        formData.incluir_telefono_consultorio === 'si' &&
        !formData.telefono_consultorio.trim()
      ) {
        alert('Por favor completá el teléfono o WhatsApp del consultorio.');
        return false;
      }

      const notificationDate = parseLocalDate(formData.fecha_notificacion);
      const appointmentDate = parseLocalDate(formData.day);
      const todayOnly = getTodayOnly();

      if (!notificationDate || !appointmentDate) {
        alert('La fecha del turno o de notificación no es válida.');
        return false;
      }

      if (notificationDate < todayOnly) {
        alert('La fecha de notificación no puede ser anterior a hoy.');
        return false;
      }

      if (notificationDate > appointmentDate) {
        alert('La fecha de notificación no puede ser posterior a la fecha del turno.');
        return false;
      }

      if (
        turnoYaComenzoOPaso({
          fecha_turno: formData.day,
          hora_inicio: formData.startTime
        })
      ) {
        alert('El turno ya comenzó o ya pasó. Por favor eliminá la notificación para poder guardar el turno.');
        return false;
      }
    }

    const repeatActual = normalizeRepeatValue(
      appointmentToEdit?.repeat_type ||
      appointmentToEdit?.repeat ||
      appointmentToEdit?.repetir_turno ||
      appointmentToEdit?.tipo_repeticion ||
      'no'
    );

    const repeatFormulario = normalizeRepeatValue(formData.repeat);

    const estadoTurnoParaSubmit = String(
      formData.estado ||
      appointmentToEdit?.estado ||
      'PENDIENTE'
    )
      .trim()
      .toUpperCase();

    const estadoBloqueaRepeatSubmit = [
      'REALIZADO',
      'CANCELADO',
      'NO_ASISTIO',
      'BORRADO'
    ].includes(estadoTurnoParaSubmit);

    const puedeModificarRepeticionEnSubmit = Boolean(
      isEditMode &&
      !appointmentStartedOrPassed &&
      !estadoBloqueaRepeatSubmit &&
      (
        appointmentToEdit?.puede_modificar_condiciones_serie ||
        !isSeriesTurn
      )
    );

    const modificarCondicionesSerie = Boolean(
      isEditMode &&
      puedeModificarRepeticionEnSubmit &&
      repeatFormulario !== repeatActual
    );

    const modalidadTurno = formData.modalidad_turno || null;

    const tipoTurnoNormalizado = normalizeTurnType(formData.tipo_turno);

    const consultorioIdTurno =
      modalidadTurno === 'presencial' &&
      tipoTurnoNormalizado === TURN_TYPES.SESION_PACIENTE &&
      formData.consultorio_id
        ? Number(formData.consultorio_id)
        : null;

    const appointmentData = {
      mode,
      id_turno: appointmentToEdit?.id_turno || appointmentToEdit?.id || null,
      id_paciente: selectedPatientId,
      id_patiente: selectedPatientId,

      tipo_turno: tipoTurnoNormalizado,

      comentario: formData.comentario || '',
      fecha_turno: formData.day,
      fecha_turno_original: appointmentToEdit?.fecha_turno || formData.day,

      hora_inicio: formData.startTime,
      hora_fin: formData.endTime,

      fecha_notificacion:
        formData.notificar === 'si'
          ? formData.fecha_notificacion
          : '',

      link_reunion:
        modalidadTurno === 'virtual'
          ? formData.meetingLink || ''
          : '',

      honorarios: formData.honorarios ?? '',

      email_1: formData.email_1.trim(),
      email_2: formData.email_2.trim(),

      notificar: formData.notificar || 'no',

      notificar_cantidad:
        formData.notificar === 'si'
          ? Number(formData.notificar_cantidad)
          : 0,

      modalidad_turno: modalidadTurno,
      consultorio_id: consultorioIdTurno,

      direccion_consultorio:
        modalidadTurno === 'presencial' && formData.notificar === 'si'
          ? formData.direccion_consultorio.trim()
          : '',

      incluir_telefono_consultorio:
        formData.incluir_telefono_consultorio || 'no',

      telefono_consultorio:
        formData.incluir_telefono_consultorio === 'si'
          ? formData.telefono_consultorio.trim()
          : '',

      estado: formData.estado || 'PENDIENTE',

      aplicar_serie_desde_este_turno:
        formData.aplicar_serie_desde_este_turno || false,

      repeat_group_id: appointmentToEdit?.repeat_group_id || null,

      repeat: repeatFormulario,
      repeat_type: repeatFormulario,

      modificar_condiciones_serie: modificarCondicionesSerie
    };

    const success = await onSubmit(appointmentData);

    if (success) {
      setFormData(createInitialFormData());
    }

    return success;
  };

  const handleDelete = async () => {
    if (!isEditMode || !appointmentToEdit || !onDelete) {
      return;
    }

    const idTurno = appointmentToEdit.id_turno || appointmentToEdit.id;

    if (!idTurno) {
      alert('No se encontró el turno que querés borrar.');
      return;
    }

    await onDelete({
      id_turno: idTurno,
      aplicar_serie_desde_este_turno: formData.aplicar_serie_desde_este_turno,
      repeat_group_id: appointmentToEdit.repeat_group_id || null,
      fecha_turno: appointmentToEdit.fecha_turno || formData.day
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={currentModalStyles}
      contentLabel={isEditMode ? 'Editar turno' : 'Agregar turno'}
    >
      <StyledModalDynamic>
        <CalendarModalCard>
          <CalendarModalCloseButton onClick={onClose} aria-label="Cerrar modal">
            <CloseIcon width={20} height={20} />
          </CalendarModalCloseButton>

          <CalendarModalTitle>
            {isEditMode ? 'Editar turno' : 'Agregar turno'}
          </CalendarModalTitle>

          <CalendarModalDescription>
            {isEditMode
              ? 'Modifica los datos del turno seleccionado.'
              : 'Completa los datos para registrar una nueva cita en el calendario.'}
          </CalendarModalDescription>

          <CalendarFormLayout>
            <CalendarField data-span="full">
              <CalendarFieldLabel>
                Tipo de evento *
              </CalendarFieldLabel>

              <TurnTypeOptionsGrid>
                {TURN_TYPE_OPTIONS.map((turnTypeOption) => {
                  const isActive =
                    selectedTurnType === turnTypeOption.value;

                  return (
                    <TurnTypeOptionButton
                      key={turnTypeOption.value}
                      type="button"
                      $active={isActive}
                      onClick={() => handleTurnTypeChange(turnTypeOption.value)}
                    >
                      <TurnTypeOptionTitle>
                        {turnTypeOption.label}
                      </TurnTypeOptionTitle>

                      <TurnTypeOptionDescription>
                        {getTurnTypeDescription(turnTypeOption.value)}
                      </TurnTypeOptionDescription>
                    </TurnTypeOptionButton>
                  );
                })}
              </TurnTypeOptionsGrid>
            </CalendarField>

            <CalendarField data-span="full">
              <CalendarFieldLabel htmlFor="calendar-patient">
                {patientLabel}
              </CalendarFieldLabel>

              {isEditMode ? (
                <CalendarInput
                  id="calendar-patient"
                  type="text"
                  value={formData.patient ?? ''}
                  disabled
                />
              ) : (
                <PatientSearch
                  value={formData.patient ?? ''}
                  patients={pacientes}
                  onChange={handlePatientChange}
                  onSelect={handlePatientSelect}
                  placeholder={patientPlaceholder}
                />
              )}

              {!isPatientSession && (
                <small style={{ color: '#666' }}>
                  La reunión queda asociada a este paciente y luego podrá vincularse con su Historia Clínica.
                </small>
              )}
            </CalendarField>

            {isSeriesTurn && (
              <CalendarField data-span="full">
                <small style={{ color: '#555' }}>
                  Este turno pertenece a una serie {repeatTypeLabel}
                  {serieFechaOrigen && (
                    <>
                      , creada el {formatDisplayDate(serieFechaOrigen)}
                      {serieHoraOrigen ? ` a las ${serieHoraOrigen}` : ''}
                    </>
                  )}
                  .
                </small>
              </CalendarField>
            )}

            <CalendarField>
              <CalendarFieldLabel htmlFor="calendar-day">
                Día *
              </CalendarFieldLabel>

              <CalendarInput
                id="calendar-day"
                type="date"
                value={formData.day ?? ''}
                disabled={shouldBlockDate}
                onChange={(e) => handleInputChange('day', e.target.value)}
              />

              {shouldBlockDate && (
                <small style={{ color: '#666' }}>
                  La fecha de una serie solo puede modificarse desde el turno original si todavía no ocurrió.
                </small>
              )}
            </CalendarField>

            <CalendarField>
              <CalendarFieldLabel htmlFor="calendar-start-time">
                Comienza *
              </CalendarFieldLabel>

              <CalendarSelect
                id="calendar-start-time"
                value={formData.startTime ?? ''}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                disabled={!formData.day}
              >
                <option value="">Seleccionar hora</option>

                {timeSlots.map((timeSlot) => (
                  <option key={timeSlot} value={timeSlot}>
                    {timeSlot}
                  </option>
                ))}
              </CalendarSelect>
            </CalendarField>

            <CalendarField>
              <CalendarFieldLabel htmlFor="calendar-end-time">
                Termina *
              </CalendarFieldLabel>

              <CalendarSelect
                id="calendar-end-time"
                value={formData.endTime ?? ''}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                disabled={!formData.day}
              >
                <option value="">Seleccionar hora</option>

                {timeSlots.map((timeSlot) => (
                  <option key={timeSlot} value={timeSlot}>
                    {timeSlot}
                  </option>
                ))}
              </CalendarSelect>
            </CalendarField>

            <CalendarField data-span="full">
              <CalendarFieldLabel htmlFor="calendar-modalidad">
                Modalidad del turno *
              </CalendarFieldLabel>

              <CalendarSelect
                id="calendar-modalidad"
                value={formData.modalidad_turno || ''}
                onChange={(e) => handleInputChange('modalidad_turno', e.target.value)}
              >
                <option value="">Sin definir</option>
                <option value="presencial">Presencial</option>
                <option value="virtual">Virtual</option>
              </CalendarSelect>

              <small style={{ color: '#666' }}>
                Si el turno es virtual, podés cargar el link de reunión. Si notificás un turno presencial, se enviará la dirección del consultorio.
              </small>
            </CalendarField>

            {shouldShowConsultorioSelector && (
              <CalendarField data-span="full">
                <CalendarFieldLabel htmlFor="calendar-consultorio">
                  Consultorio / sede 
                </CalendarFieldLabel>

                <CalendarSelect
                  id="calendar-consultorio"
                  value={formData.consultorio_id || ''}
                  onChange={(e) => handleInputChange('consultorio_id', e.target.value)}
                >
                  <option value="">Seleccionar consultorio</option>

                  {consultoriosActivos.map((consultorio) => (
                    <option
                      key={consultorio.id}
                      value={consultorio.id}
                    >
                      {consultorio.es_default
                        ? `${consultorio.nombre} (default)`
                        : consultorio.nombre}
                    </option>
                  ))}
                </CalendarSelect>

                <small style={{ color: '#666' }}>
                  Al seleccionar una sede, se puede usar su dirección y teléfono para la notificación presencial.
                </small>
              </CalendarField>
            )}

            {shouldShowMeetingLink && (
              <CalendarField data-span="full">
                <CalendarFieldLabel htmlFor="calendar-meeting-link">
                  Link a la reunión
                </CalendarFieldLabel>

                <CalendarInput
                  id="calendar-meeting-link"
                  type="text"
                  placeholder="https://meet.google.com/..."
                  value={formData.meetingLink ?? ''}
                  disabled={notificationLocked && shouldShowNotificationFields}
                  onChange={(e) => handleInputChange('meetingLink', e.target.value)}
                />
              </CalendarField>
            )}

            {shouldShowNotificationFields && (
              <CalendarField>
                <CalendarFieldLabel htmlFor="calendar-fecha-notificar">
                  ¿Qué día notificamos? *
                </CalendarFieldLabel>

                <CalendarInput
                  id="calendar-fecha-notificar"
                  type="date"
                  value={formData.fecha_notificacion ?? ''}
                  min={getTodayLocalString()}
                  max={formData.day || undefined}
                  disabled={notificationLocked}
                  onChange={(e) => handleInputChange('fecha_notificacion', e.target.value)}
                />
              </CalendarField>
            )}

            {isEditMode && (
              <CalendarField data-span="full">
                <small style={{ color: '#555', display: 'block' }}>
                  <strong>Estado del email:</strong> {mensajeEstadoNotificacion}
                </small>

                {emailPrincipalNotificacion && (
                  <small style={{ color: '#555', display: 'block', marginTop: '4px' }}>
                    <strong>Email:</strong> {emailPrincipalNotificacion}
                  </small>
                )}

                {mensajeErrorNotificacion && (
                  <small style={{ color: '#B91C1C', display: 'block', marginTop: '4px' }}>
                    <strong>Detalle:</strong> {mensajeErrorNotificacion}
                  </small>
                )}

                {mensajeAccionNotificacion && (
                  <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                    {mensajeAccionNotificacion}
                  </small>
                )}

                {appointmentStartedOrPassed && canManuallyRequeueNotification(appointmentToEdit) && (
                  <small style={{ color: '#B45309', display: 'block', marginTop: '4px' }}>
                    El turno ya comenzó o ya pasó. Ya no se puede volver a poner la notificación en cola.
                  </small>
                )}

                {shouldShowSpamWarning && (
                  <small style={{ color: '#B91C1C', display: 'block', marginTop: '4px' }}>
                    No se recomienda reenviar automáticamente a una dirección que marcó el email como spam. Contactá a la persona por otro medio.
                  </small>
                )}

                {canShowManualRequeueButton && (
                  <small style={{ color: '#555', display: 'block', marginTop: '4px' }}>
                    Podés corregir el email y volver a poner la notificación en cola.
                  </small>
                )}

                {isSeriesTurn && (
                  <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                    La notificación corresponde solamente a este turno. No se replica automáticamente a toda la serie.
                  </small>
                )}
              </CalendarField>
            )}

            <CalendarField data-span="full">
              <CalendarFieldLabel htmlFor="calendar-honorarios">
                Honorarios
              </CalendarFieldLabel>

              <CurrencyInputContainer>
                <CurrencySymbol>$</CurrencySymbol>

                <CurrencyInput
                  id="calendar-honorarios"
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  value={formData.honorarios ?? ''}
                  onChange={(e) =>
                    handleInputChange('honorarios', e.target.value.replace(/\D/g, ''))
                  }
                />
              </CurrencyInputContainer>
            </CalendarField>

            {shouldShowConsultorioAddress && (
              <CalendarField data-span="full">
                <CalendarFieldLabel htmlFor="calendar-direccion-consultorio">
                  Dirección del consultorio *
                </CalendarFieldLabel>

                <CalendarInput
                  id="calendar-direccion-consultorio"
                  type="text"
                  placeholder="Ej: Av. Corrientes 1234, 2° B"
                  value={formData.direccion_consultorio ?? ''}
                  disabled={notificationLocked}
                  onChange={(e) => handleInputChange('direccion_consultorio', e.target.value)}
                />
              </CalendarField>
            )}

            {shouldShowNotificationFields && (
              <CalendarField data-span="full">
                <label
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    color: notificationLocked ? '#999' : '#555'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.incluir_telefono_consultorio === 'si'}
                    disabled={notificationLocked}
                    onChange={(e) =>
                      handleInputChange(
                        'incluir_telefono_consultorio',
                        e.target.checked ? 'si' : 'no'
                      )
                    }
                  />
                  Incluir teléfono / WhatsApp del consultorio
                </label>

                <CalendarInput
                  id="calendar-telefono-consultorio"
                  type="text"
                  placeholder="Ej: +54 9 11 1234-5678"
                  value={formData.telefono_consultorio ?? ''}
                  disabled={shouldDisableTelefonoConsultorio}
                  style={{
                    marginTop: '8px',
                    backgroundColor: shouldDisableTelefonoConsultorio ? '#f3f4f6' : '#FFFFFF',
                    color: shouldDisableTelefonoConsultorio ? '#999' : undefined
                  }}
                  onChange={(e) => handleInputChange('telefono_consultorio', e.target.value)}
                />

                <small style={{ color: '#666' }}>
                  Si lo tildás, se enviará como contacto para reprogramar el turno.
                </small>
              </CalendarField>
            )}

            <CalendarField data-span="full">
              <CalendarFieldLabel htmlFor="calendar-repeat">
                Repetir
              </CalendarFieldLabel>

              <CalendarSelect
                id="calendar-repeat"
                value={formData.repeat ?? 'no'}
                disabled={shouldBlockRepeat}
                onChange={(e) => handleInputChange('repeat', e.target.value)}
              >
                <option value="no">No repetir</option>
                <option value="semanal">Semanalmente</option>
                <option value="quincenal">Quincenalmente</option>
              </CalendarSelect>

              {isEditMode && isSeriesTurn && !canModifySeriesConditions && (
                <small style={{ color: '#666' }}>
                  La frecuencia de una serie solo puede modificarse desde el turno original si todavía no ocurrió.
                </small>
              )}
            </CalendarField>

            {isEditMode && (
              <CalendarField data-span="full">
                <CalendarFieldLabel htmlFor="calendar-estado">
                  Estado del turno
                </CalendarFieldLabel>

                <CalendarSelect
                  id="calendar-estado"
                  value={formData.estado ?? 'PENDIENTE'}
                  onChange={(e) => handleInputChange('estado', e.target.value)}
                >
                  <option value="PENDIENTE">Pendiente</option>
                  <option value="REALIZADO">Realizado</option>
                  <option value="CANCELADO">Cancelado</option>
                  <option value="NO_ASISTIO">No asistió</option>
                </CalendarSelect>
              </CalendarField>
            )}

            <CalendarField data-span="full">
              <CalendarFieldLabel htmlFor="calendar-notificar">
                ¿Notificar por email? {formData.notificar === 'si' ? '¿Cuántas personas?' : null}
              </CalendarFieldLabel>

              <CalendarField style={{ flexDirection: 'row' }}>
                <CalendarSelect
                  id="calendar-notificar"
                  value={formData.notificar ?? 'no'}
                  disabled={shouldDisableNotificationToggle}
                  onChange={(e) => handleInputChange('notificar', e.target.value)}
                >
                  <option value="no">No</option>
                  <option value="si">Sí</option>
                </CalendarSelect>

                {shouldShowNotificationFields && (
                  <CalendarSelect
                    id="calendar-notificar_cantidad"
                    value={formData.notificar_cantidad ?? '1'}
                    disabled={notificationLocked}
                    onChange={(e) => handleInputChange('notificar_cantidad', e.target.value)}
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                  </CalendarSelect>
                )}
              </CalendarField>
            </CalendarField>

            <CalendarField data-span="full">
              {shouldShowNotificationFields && (
                <CalendarInput
                  id="calendar-email_1"
                  type="email"
                  placeholder="Escriba el email del tutor para enviar turno"
                  value={formData.email_1 ?? ''}
                  disabled={notificationLocked}
                  onChange={(e) => handleInputChange('email_1', e.target.value)}
                />
              )}

              {shouldShowSecondEmail && (
                <CalendarInput
                  id="calendar-email_2"
                  type="email"
                  placeholder="Escriba el email del segundo tutor para enviar turno"
                  value={formData.email_2 ?? ''}
                  disabled={notificationLocked}
                  onChange={(e) => handleInputChange('email_2', e.target.value)}
                />
              )}
            </CalendarField>

            <CalendarField data-span="full">
              <CalendarFieldLabel htmlFor="calendar-comentario">
                Comentario
              </CalendarFieldLabel>

              <CalendarTextarea
                id="calendar-comentario"
                rows={3}
                maxLength={100}
                placeholder="Comentario (máximo 100 caracteres)"
                value={formData.comentario ?? ''}
                onChange={(e) => handleInputChange('comentario', e.target.value)}
              />

              <small style={{ color: '#666' }}>
                {(formData.comentario || '').length}/100
              </small>
            </CalendarField>

            {isEditMode && isSeriesTurn && (
              <CalendarField data-span="full">
                <div
                  style={{
                    border: '1px solid #F1D6A6',
                    background: '#FFF8ED',
                    borderRadius: '12px',
                    padding: '12px 14px'
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      gap: '9px',
                      alignItems: 'flex-start',
                      color: '#5C4118',
                      fontWeight: 700,
                      lineHeight: 1.35
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(formData.aplicar_serie_desde_este_turno)}
                      onChange={(e) =>
                        handleInputChange('aplicar_serie_desde_este_turno', e.target.checked)
                      }
                      style={{
                        marginTop: '3px'
                      }}
                    />

                    <span>
                      Aplicar cambios a esta serie desde este turno en adelante
                    </span>
                  </label>

                  <small
                    style={{
                      color: '#7A5A22',
                      display: 'block',
                      marginTop: '6px',
                      paddingLeft: '25px',
                      lineHeight: 1.35
                    }}
                  >
                    Los turnos anteriores no se modificarán. Las notificaciones no se modificarán automáticamente.
                  </small>
                </div>
              </CalendarField>
            )}
          </CalendarFormLayout>

          <CalendarModalActions
            style={{
              marginBottom: 0,
              marginTop: 0,
              paddingBottom: 0
            }}
          >
            <BotonNaranja type="button" onClick={handleSubmit}>
              {isEditMode ? 'GUARDAR CAMBIOS' : 'CREAR'}
            </BotonNaranja>

            {canShowManualRequeueButton && (
              <CancelButton
                type="button"
                onClick={handleManualRequeueNotification}
              >
                {getManualRequeueButtonText(appointmentToEdit)}
              </CancelButton>
            )}

            {shouldShowClinicalHistoryButton && (
              <CancelButton
                type="button"
                onClick={() => onOpenClinicalHistory(appointmentToEdit)}
              >
                IR A HISTORIA CLÍNICA
              </CancelButton>
            )}

            {shouldShowPatientStatsButton && (
              <CancelButton
                type="button"
                onClick={() => onOpenPatientStats(appointmentToEdit)}
              >
                ESTADÍSTICA Y COBROS
              </CancelButton>
            )}

            {isEditMode && canDeleteAppointment && (
              <CancelButton
                type="button"
                onClick={handleDelete}
              >
                BORRAR TURNO
              </CancelButton>
            )}

            {isMobile && (
              <CancelButton
                type="button"
                onClick={onClose}
              >
                CANCELAR
              </CancelButton>
            )}
          </CalendarModalActions>

          {isEditMode && !canDeleteAppointment && (
            <div
              style={{
                margin: '4px auto 0',
                maxWidth: '520px',
                padding: '8px 12px',
                borderRadius: '9px',
                background: '#F8FAFC',
                border: '1px solid #E5E7EB',
                color: '#666',
                fontSize: '11.5px',
                lineHeight: 1.3,
                textAlign: 'center'
              }}
            >
              Este turno no se puede borrar porque ya comenzó, ya pasó o tiene una notificación procesada.
            </div>
          )}
        </CalendarModalCard>
      </StyledModalDynamic>
    </Modal>
  );
};

export default CalendarModal;